import { z } from 'zod';
import {
  COMMISSIONING_CLUSTER_ENTITY_TYPES,
  type CommissioningClusterEntityType,
  type CommissioningCompetitionFamily,
  type CommissioningInstanceScope,
} from '../types/commissioning';

const uuid = z.string().uuid().transform((value) => value.toLowerCase());
const revision = z.number().int().positive().max(2_147_483_647);
export const commissioningMaturationModeSchema = z.enum(['individual', 'collective']);
export type CommissioningMaturationMode = z.infer<typeof commissioningMaturationModeSchema>;
/**
 * The only economic contract of a competition: economic subject, period,
 * roster and frozen context. `economic_context_version` is a stored invariant
 * (always 2, enforced by the database CHECK), not a discriminator. Source
 * admission and the kernel/template versions are different contracts.
 */
export const commissioningCommonEconomySchema = z.object({
  economic_context_version: z.literal(2), participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  maturation_mode: commissioningMaturationModeSchema, context_revision: revision,
}).strict();
export type CommissioningCommonEconomy = z.infer<typeof commissioningCommonEconomySchema>;

export function commissioningParticipantTypeAllowed(
  family: CommissioningCompetitionFamily, participantType: CommissioningClusterEntityType,
): boolean {
  return family === 'risorse' ? participantType === 'risorsa' : participantType !== 'risorsa';
}

/** A derived storage value, not a second selector deciding who earns. */
export function commissioningCommonInstanceScope(context: CommissioningCommonEconomy): CommissioningInstanceScope {
  switch (context.participant_type) {
    case 'rs': return context.maturation_mode === 'collective' ? 'multi_rs' : 'rs';
    case 'pdv': return context.maturation_mode === 'collective' ? 'multi_pdv' : 'pdv';
    case 'risorsa': return context.maturation_mode === 'collective' ? 'multi_user' : 'user';
  }
}

const participantBase = {
  id: uuid, tenant_id: uuid, competition_id: uuid,
  created_revision: revision, is_active: z.boolean(),
  /** Display only; never used as identity or payment beneficiary. */
  label: z.string().min(1),
};
export const commissioningCompetitionParticipantSchema = z.discriminatedUnion('entity_type', [
  z.object({
    ...participantBase, entity_type: z.literal('rs'), company_id: uuid, store_id: z.null(), user_id: z.null(),
    store_admission: z.null(), allowed_store_ids: z.array(uuid).max(0),
  }).strict(),
  z.object({
    ...participantBase, entity_type: z.literal('pdv'), company_id: z.null(), store_id: uuid, user_id: z.null(),
    store_admission: z.null(), allowed_store_ids: z.array(uuid).max(0),
  }).strict(),
  z.object({
    ...participantBase, entity_type: z.literal('risorsa'), company_id: z.null(), store_id: z.null(), user_id: uuid,
    store_admission: z.enum(['anywhere', 'selected']), allowed_store_ids: z.array(uuid),
  }).strict(),
]);
export type CommissioningCompetitionParticipant = z.infer<typeof commissioningCompetitionParticipantSchema>;

const subjectBase = {
  id: uuid, tenant_id: uuid, competition_id: uuid, created_revision: revision,
};
export const commissioningEconomicSubjectSchema = z.discriminatedUnion('kind', [
  z.object({ ...subjectBase, kind: z.literal('individual'), participant_id: uuid }).strict(),
  z.object({ ...subjectBase, kind: z.literal('collective'), participant_id: z.null() }).strict(),
]);
export type CommissioningEconomicSubject = z.infer<typeof commissioningEconomicSubjectSchema>;

/** One economic owner, also used by period results without a single contributor. */
export const commissioningEconomicOwnershipSchema = z.object({
  context_revision: revision,
  economic_subject_id: uuid,
  participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  maturation_mode: commissioningMaturationModeSchema,
}).strict();
export type CommissioningEconomicOwnership = z.infer<typeof commissioningEconomicOwnershipSchema>;
/** An event's contribution is not an individual entitlement to the collective award. */
export const commissioningEconomicAttributionSchema = commissioningEconomicOwnershipSchema.extend({ participant_id: uuid });
export type CommissioningEconomicAttribution = z.infer<typeof commissioningEconomicAttributionSchema>;

const storeIds = z.array(uuid).max(5000).refine(
  (ids) => new Set(ids).size === ids.length, 'Store duplicati',
);
export const commissioningParticipantInputSchema = z.object({
  entity_id: uuid,
  store_admission: z.enum(['anywhere', 'selected']).optional(),
  allowed_store_ids: storeIds.optional(),
}).strict();
export type CommissioningParticipantInput = z.infer<typeof commissioningParticipantInputSchema>;

function validateRosterInput(
  entries: CommissioningParticipantInput[], ctx: z.RefinementCtx, prefix: string,
): void {
  const ids = new Set<string>();
  let links = 0;
  for (const [index, entry] of entries.entries()) {
    if (ids.has(entry.entity_id)) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: [prefix, index, 'entity_id'], message: 'Partecipante duplicato',
    });
    ids.add(entry.entity_id);
    links += entry.allowed_store_ids?.length ?? 0;
  }
  if (links > 50_000) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: [prefix], message: 'Superato il limite di 50.000 abbinamenti risorsa-store per operazione',
  });
}

/** Initial draft only. The type belongs to the competition, not to each entry. */
export const commissioningCompetitionContextInputSchema = z.object({
  participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  maturation_mode: commissioningMaturationModeSchema,
  participants: z.array(commissioningParticipantInputSchema).max(5000).default([]),
}).strict().superRefine((value, ctx) => {
  validateRosterInput(value.participants, ctx, 'participants');
  for (const [index, participant] of value.participants.entries()) {
    if (value.participant_type !== 'risorsa'
      && (participant.store_admission !== undefined || (participant.allowed_store_ids?.length ?? 0) !== 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['participants', index], message: 'Vincoli store ammessi soltanto per Risorse' });
    }
    if (value.participant_type === 'risorsa') {
      const selected = participant.store_admission === 'selected';
      const hasStores = (participant.allowed_store_ids?.length ?? 0) > 0;
      if (selected !== hasStores) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: ['participants', index, 'allowed_store_ids'],
        message: 'Selezionare gli store espliciti oppure scegliere attività ovunque',
      });
    }
  }
});
export type CommissioningCompetitionContextInput = z.infer<typeof commissioningCompetitionContextInputSchema>;

/** Differential commands: omitted/page-hidden participants are never removed. */
export const commissioningCompetitionRosterWriteSchema = z.object({
  expected_revision: revision,
  /** Active competition: the caller has read the impact preview of this revision. */
  confirm_revision_impact: z.boolean().optional(),
  changes: z.array(commissioningParticipantInputSchema.extend({ is_active: z.boolean() })).min(1).max(5000),
}).strict().superRefine((value, ctx) => {
  validateRosterInput(value.changes, ctx, 'changes');
  for (const [index, entry] of value.changes.entries()) {
    if (!entry.is_active && (entry.store_admission !== undefined || entry.allowed_store_ids !== undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['changes', index], message: 'La rimozione conserva i vincoli storici, non li modifica' });
    }
  }
});
export type CommissioningCompetitionRosterWrite = z.infer<typeof commissioningCompetitionRosterWriteSchema>;
export const commissioningCompetitionIdParamsSchema = z.object({ id: uuid });
export const commissioningCompetitionRosterQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
  include_retired: z.enum(['true', 'false']).default('false').transform((value) => value === 'true'),
});
export type CommissioningCompetitionRosterQuery = z.infer<typeof commissioningCompetitionRosterQuerySchema>;
export const commissioningCompetitionRosterPageSchema = z.object({
  competition_id: uuid,
  family: z.enum(['insegna', 'sellin', 'risorse']),
  context: commissioningCommonEconomySchema,
  participants: z.array(commissioningCompetitionParticipantSchema),
  subjects: z.array(commissioningEconomicSubjectSchema),
  total: z.number().int().nonnegative(),
  active_count: z.number().int().nonnegative(),
  visibility_limited: z.boolean(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive().max(200),
}).strict();
export type CommissioningCompetitionRosterPage = z.infer<typeof commissioningCompetitionRosterPageSchema>;
export const commissioningParticipantOptionsQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
  target: z.enum(['participants', 'stores']).default('participants'),
}).strict();
export type CommissioningParticipantOptionsQuery = z.infer<typeof commissioningParticipantOptionsQuerySchema>;
export const commissioningParticipantOptionSchema = z.object({
  entity_id: uuid, label: z.string().min(1), secondary_label: z.string().nullable(),
  is_enrolled: z.boolean(),
}).strict();
export type CommissioningParticipantOption = z.infer<typeof commissioningParticipantOptionSchema>;
export const commissioningParticipantOptionsPageSchema = z.object({
  competition_id: uuid, context: commissioningCommonEconomySchema,
  entity_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  target: z.enum(['participants', 'stores']),
  items: z.array(commissioningParticipantOptionSchema),
  total: z.number().int().nonnegative(), page: z.number().int().positive(),
  page_size: z.number().int().positive().max(200), visibility_limited: z.boolean(),
}).strict().superRefine((value, ctx) => {
  if (value.entity_type !== (value.target === 'stores' ? 'pdv' : value.context.participant_type)
    || value.target === 'stores' && value.context.participant_type !== 'risorsa') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['entity_type'], message: 'Catalogo incompatibile con il contesto gara' });
  }
  if (new Set(value.items.map((item) => item.entity_id)).size !== value.items.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'Identità duplicate nel catalogo' });
  }
});
export type CommissioningParticipantOptionsPage = z.infer<typeof commissioningParticipantOptionsPageSchema>;
/** Outcome of a confirmed revision on an active competition: the photograph is
 * committed with the mutation; queue delivery is reported separately per state. */
export const commissioningCompetitionContextRevisionOutcomeSchema = z.object({
  previous_revision: revision,
  /** Configurators re-photographed inside the same transaction (published versions appended). */
  republished_instance_ids: z.array(uuid),
  recalc: z.enum(['not_required', 'queued', 'pending', 'total_queued', 'total_pending']),
  recalc_run_ids: z.array(uuid),
  message: z.string().min(1),
}).strict();
export type CommissioningCompetitionContextRevisionOutcome = z.infer<typeof commissioningCompetitionContextRevisionOutcomeSchema>;
export const commissioningCompetitionRosterMutationSchema = z.object({
  context: commissioningCommonEconomySchema,
  changed: z.boolean(),
  revision: commissioningCompetitionContextRevisionOutcomeSchema.nullable().optional(),
}).strict();
export type CommissioningCompetitionRosterMutation = z.infer<typeof commissioningCompetitionRosterMutationSchema>;

/** Read-only impact of a common-context revision, shown before the explicit confirmation.
 * Counts only: no hidden participant identities. */
export const commissioningCompetitionContextImpactSchema = z.object({
  competition_id: uuid,
  status: z.enum(['draft', 'active', 'suspended', 'closed']),
  context: commissioningCommonEconomySchema,
  /** draft: free edit; revision: explicit confirmation + photograph + targeted recalculation; locked: no write. */
  write_mode: z.enum(['draft', 'revision', 'locked']),
  published_instances: z.array(z.object({
    id: uuid, name: z.string().min(1), type_code: z.string().min(1), rules_version: z.number().int().nonnegative(),
    is_active: z.boolean(),
    /** Declarative Soglie recipes replay only the affected subjects; others need the existing total recalculation. */
    recalculation: z.enum(['scoped', 'total', 'none']),
  })),
  active_participants: z.number().int().nonnegative(),
  classification_members: z.number().int().nonnegative(),
  member_targets: z.number().int().nonnegative(),
  open_periods: z.number().int().nonnegative(),
  locked_periods: z.number().int().nonnegative(),
  pending_runs: z.number().int().nonnegative(),
}).strict();
export type CommissioningCompetitionContextImpact = z.infer<typeof commissioningCompetitionContextImpactSchema>;

/** Draft-only change of participant type / maturation. Preview first, then apply. */
export const commissioningCompetitionContextChangeSchema = z.object({
  expected_revision: revision,
  participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES).optional(),
  maturation_mode: commissioningMaturationModeSchema.optional(),
  apply: z.boolean().default(false),
}).strict().refine((value) => value.participant_type !== undefined || value.maturation_mode !== undefined, {
  message: 'Indicare il nuovo tipo partecipante o la nuova maturazione',
});
export type CommissioningCompetitionContextChange = z.infer<typeof commissioningCompetitionContextChangeSchema>;
export type CommissioningCompetitionContextChangeInput = z.input<typeof commissioningCompetitionContextChangeSchema>;
export const commissioningCompetitionContextChangePreviewSchema = z.object({
  competition_id: uuid,
  current: commissioningCommonEconomySchema,
  next: z.object({ participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES), maturation_mode: commissioningMaturationModeSchema }).strict(),
  /** Blocking rows are never deleted by the apply: the caller resolves them through the existing editors. */
  incompatibilities: z.array(z.object({
    kind: z.enum(['participants_other_type', 'classifications_other_type', 'memberships_other_maturation',
      'member_targets_other_maturation', 'published_history', 'family_type_mismatch']),
    count: z.number().int().nonnegative(),
    message: z.string().min(1),
  }).strict()),
  /** Derived storage realigned by the apply, not data loss. */
  rescoped_instances: z.array(z.object({ id: uuid, name: z.string().min(1), from: z.string().min(1), to: z.string().min(1) }).strict()),
  can_apply: z.boolean(),
  applied: z.boolean(),
  context: commissioningCommonEconomySchema,
}).strict();
export type CommissioningCompetitionContextChangePreview = z.infer<typeof commissioningCompetitionContextChangePreviewSchema>;

/** Server-owned photograph inside existing rule versions, never a template payload.
 * This is the common economic context; classification/rules retain their own
 * existing snapshots. An empty/incomplete draft is not a published photograph.
 */
export const commissioningCompetitionContextSnapshotSchema = z.object({
  schemaVersion: z.literal(1), capturedAt: z.string().datetime({ offset: true }),
  competition_id: uuid, tenant_id: uuid,
  family: z.enum(['insegna', 'sellin', 'risorse']),
  retail_brand_id: uuid.nullable(), channel_id: uuid.nullable(),
  period_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  period_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  participant_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  maturation_mode: commissioningMaturationModeSchema,
  context_revision: revision,
  participants: z.array(commissioningCompetitionParticipantSchema).min(1),
  subjects: z.array(commissioningEconomicSubjectSchema).min(1),
}).strict().superRefine((value, ctx) => {
  const issue = (path: Array<string | number>, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
  if (!commissioningParticipantTypeAllowed(value.family, value.participant_type)) {
    issue(['participant_type'], 'Tipo partecipante incompatibile con la famiglia');
  }
  if (value.family === 'risorse'
    ? value.retail_brand_id !== null || value.channel_id !== null
    : value.retail_brand_id === null || value.channel_id === null) {
    issue(['retail_brand_id'], 'Il contesto non sostituisce i vincoli commerciali della famiglia');
  }
  if (value.period_start > value.period_end) issue(['period_end'], 'Validità invertita');
  const participantIds = new Set<string>();
  const entityIds = new Set<string>();
  for (const [index, participant] of value.participants.entries()) {
    if (participantIds.has(participant.id)) issue(['participants', index, 'id'], 'Iscrizione duplicata');
    participantIds.add(participant.id);
    const entityId = participant.company_id ?? participant.store_id ?? participant.user_id;
    if (entityId === null || entityIds.has(entityId)) issue(['participants', index], 'Identità partecipante duplicata o mancante');
    else entityIds.add(entityId);
    if (participant.tenant_id !== value.tenant_id || participant.competition_id !== value.competition_id
      || participant.entity_type !== value.participant_type || !participant.is_active
      || participant.created_revision > value.context_revision) {
      issue(['participants', index], 'Iscrizione estranea o incompatibile con la revisione');
    }
    if (new Set(participant.allowed_store_ids).size !== participant.allowed_store_ids.length) {
      issue(['participants', index, 'allowed_store_ids'], 'Store duplicati');
    }
    if (participant.entity_type === 'risorsa'
      && ((participant.store_admission === 'selected' && participant.allowed_store_ids.length === 0)
        || (participant.store_admission === 'anywhere' && participant.allowed_store_ids.length !== 0))) {
      issue(['participants', index, 'allowed_store_ids'], 'Ammissione store incompleta o contraddittoria');
    }
  }
  const subjectIds = new Set<string>();
  const owners = new Set<string>();
  for (const [index, subject] of value.subjects.entries()) {
    if (subjectIds.has(subject.id)) issue(['subjects', index, 'id'], 'Identità economica duplicata');
    subjectIds.add(subject.id);
    if (subject.tenant_id !== value.tenant_id || subject.competition_id !== value.competition_id
      || subject.kind !== value.maturation_mode || subject.created_revision > value.context_revision) {
      issue(['subjects', index], 'Soggetto estraneo o incompatibile con la revisione');
    }
    if (subject.kind === 'individual') {
      if (!participantIds.has(subject.participant_id) || owners.has(subject.participant_id)) {
        issue(['subjects', index, 'participant_id'], 'Proprietà individuale mancante o duplicata');
      }
      owners.add(subject.participant_id);
    }
  }
  if (value.maturation_mode === 'collective' ? value.subjects.length !== 1 : owners.size !== participantIds.size) {
    issue(['subjects'], 'La maturazione richiede un solo collettivo oppure un soggetto per ogni partecipante');
  }
});
export type CommissioningCompetitionContextSnapshot = z.infer<typeof commissioningCompetitionContextSnapshotSchema>;

/** Configuration diagnostics disclose no hidden participant identities. */
export const commissioningEconomicContextFindingSchema = z.object({
  kind: z.literal('economic_context'), severity: z.literal('error'),
  instanceId: uuid.optional(),
  reason: z.enum([
    'empty_roster', 'configuration_missing', 'scope_conflict', 'classification_incomplete',
    'inventory_limit',
  ]),
  message: z.string().min(1),
}).strict();
export type CommissioningEconomicContextFinding = z.infer<typeof commissioningEconomicContextFindingSchema>;
